import * as React from "react";

/**
 * Runs `onKey` for a window keydown that `match` accepts: a key name ("Escape")
 * or a predicate (`(e) => isBareKeyShortcut(e, "d")`). The listener follows
 * `enabled` and is removed on unmount; `onKey` may change every render.
 * `capture` runs it before every bubble-phase listener (a page's own `document`
 * shortcut), so a `preventDefault()` here claims the key (BRE-9244).
 */
export function useHotkey(
  match: string | ((event: KeyboardEvent) => boolean),
  onKey: (event: KeyboardEvent) => void,
  { enabled = true, capture = false }: { enabled?: boolean; capture?: boolean } = {}
): void {
  const latest = React.useRef({ match, onKey });
  React.useEffect(() => {
    latest.current = { match, onKey };
  });

  React.useEffect(() => {
    if (!enabled) return;
    const handler = (event: KeyboardEvent) => {
      // A plain `Event("keydown")` (browser autofill, extensions) has no `key`; predicates and handlers assume it (BRE-9884).
      if (event.key === undefined) return;
      const { match: m, onKey: run } = latest.current;
      if (typeof m === "string" ? event.key === m : m(event)) run(event);
    };
    window.addEventListener("keydown", handler, { capture });
    return () => window.removeEventListener("keydown", handler, { capture });
  }, [enabled, capture]);
}
